import { Stack } from "@mui/system"
import { makeStyles } from "tss-react/mui"
import { faAngleDown } from "@fortawesome/pro-light-svg-icons"
import { Button, Popover, Box, Typography, ButtonProps, Tooltip, PopoverOrigin, BoxProps } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { STATUS_MAP } from "../constants"
import { AVAILABLE_STATUSES, FilteredStatuses, MeetingServiceOrderStatus } from "../meetingServiceTypes"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"

const useStatusMenuClasses = makeStyles()((theme) => ({
    listRoot: {
        gap: theme.spacing(1),
        padding: theme.spacing(1.5),
        "& .MuiListItemButton-root": {
            borderRadius: theme.shape.borderRadius,
            padding: 0,
        },
    },
}))

const getStatusDetails = (status: FilteredStatuses) =>
    STATUS_MAP[status] ?? STATUS_MAP[MeetingServiceOrderStatus.DEFAULT]

type ChipSize = "small" | "medium" | "large"

const useStyles = makeStyles<{ status: FilteredStatuses }>()((theme, { status }) => ({
    statusItemRoot: {
        display: "flex",
        cursor: "pointer",
        alignItems: "center",
        gap: theme.spacing(1.25),
        padding: theme.spacing(1.5, 1.75),
        width: "fit-content",
        fontSize: "0.875rem",
        fontWeight: theme.typography.fontWeightMedium,
        backgroundColor: getStatusDetails(status).bgColor,
        color: getStatusDetails(status).textColor,
        border: `1px solid ${getStatusDetails(status).borderColor}`,
        "&:hover": {
            "& > div": {
                opacity: 0.8,
            },
        },
    },
    buttonLabel: {
        fontSize: "0.875rem",
        fontWeight: 500,
        whiteSpace: "nowrap",
    },
    buttonMinimized: {
        padding: theme.spacing(0.5, 1),
        borderRadius: "0.5rem",
        width: "fit-content",
        height: "fit-content",
    },
    buttonMinimizedSmall: {
        padding: "0.25rem",
        borderRadius: "0.375rem",
    },
    buttonMinimizedLarge: {
        padding: theme.spacing(0.75, 1.25),
        borderRadius: "0.625rem",
    },
    buttonMinimizedIconToggle: {
        color: tailwindPalette.gray[700],
    },
    buttonMinimizedLabel: {
        textTransform: "capitalize",
        whiteSpace: "nowrap",
    },
    buttonMinimizedLabelSmall: {
        fontSize: "0.625rem",
        fontWeight: 500,
    },
    buttonMinimizedLabelLarge: {
        fontSize: "0.875rem",
    },
    disabled: {
        cursor: "not-allowed",
    },
    minWidthSize: {
        minWidth: "9.25rem",
    },
}))

type PopoverPlacement = "bottom" | "top"

const POPOVER_PLACEMENT: {
    [key in PopoverPlacement]: {
        anchorOrigin: PopoverOrigin
        transformOrigin: PopoverOrigin
    }
} = {
    bottom: {
        anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
        },
        transformOrigin: {
            vertical: "top",
            horizontal: "right",
        },
    },
    top: {
        anchorOrigin: {
            vertical: "top",
            horizontal: "right",
        },
        transformOrigin: {
            vertical: "bottom",
            horizontal: "right",
        },
    },
}

interface StatusButtonProps {
    hiddenStatuses?: MeetingServiceOrderStatus[]
    currentStatus?: FilteredStatuses
    onStatusChange: (status: FilteredStatuses) => void
    anchorEl?: {
        top: number
        left: number
    } | null
    handleClose: () => void
    placement?: PopoverPlacement
}

interface StatusItemProps {
    variant?: "action" | "state"
    status: FilteredStatuses
    className?: string
    onClick?: (status: FilteredStatuses) => void
}

export function StatusItem({ status, className, variant = "action", onClick = () => {} }: StatusItemProps) {
    const { classes, cx } = useStyles({ status })
    const { label, icon, stateTranslationId, actionTranslationId, disabled } = getStatusDetails(status)

    return (
        <Box
            onClick={() => !disabled && onClick(status)}
            className={cx(classes.statusItemRoot, classes.minWidthSize, disabled && classes.disabled, className)}
        >
            <FontAwesomeIcon icon={icon} size="lg" />
            <LocalizedStrict id={variant === "action" ? actionTranslationId : stateTranslationId}>
                <Typography variant="body1" component="span" className={classes.buttonLabel}>
                    {label}
                </Typography>
            </LocalizedStrict>
        </Box>
    )
}

type MinimizedStatusChipProps = BoxProps & {
    status: FilteredStatuses
    tooltipLabel: string
    size?: ChipSize
    labelSizeOverride?: string
}

export const SimpleStatusChip = ({
    status,
    tooltipLabel,
    size = "medium",
    className,
    labelSizeOverride,
    ...rest
}: MinimizedStatusChipProps) => {
    const { classes, cx } = useStyles({ status })
    const { label, stateTranslationId, disabled } = getStatusDetails(status)

    const sizeClasses = {
        small: classes.buttonMinimizedSmall,
        medium: classes.buttonMinimized,
        large: classes.buttonMinimizedLarge,
    }

    const labelSizeClasses = {
        small: classes.buttonMinimizedLabelSmall,
        medium: classes.buttonMinimizedLabel,
        large: classes.buttonMinimizedLabelLarge,
    }

    return (
        <Tooltip title={tooltipLabel} placement="bottom-end">
            <Box
                {...rest}
                className={cx(classes.statusItemRoot, disabled && classes.disabled, sizeClasses[size], className)}
            >
                <LocalizedStrict id={stateTranslationId}>
                    <Typography
                        variant="body2"
                        component="span"
                        className={cx(labelSizeClasses[size], labelSizeOverride)}
                    >
                        {label}
                    </Typography>
                </LocalizedStrict>
            </Box>
        </Tooltip>
    )
}

type MinimizedStatusButtonProps = ButtonProps & {
    status: FilteredStatuses
    tooltipLabel: string
}

export const MinimizedStatusButton = ({ status, tooltipLabel, ...rest }: MinimizedStatusButtonProps) => {
    const { classes, cx } = useStyles({ status })
    const { statusItemLabel, icon, stateTranslationId, disabled } = getStatusDetails(status)

    return (
        <Tooltip title={tooltipLabel} placement="bottom-end">
            <Button {...rest} disabled={disabled} className={cx(classes.statusItemRoot, classes.buttonMinimized)}>
                <FontAwesomeIcon icon={icon} size="sm" />
                <LocalizedStrict id={stateTranslationId}>
                    <Typography variant="body2" component="span" className={classes.buttonMinimizedLabel}>
                        {statusItemLabel}
                    </Typography>
                </LocalizedStrict>
                <FontAwesomeIcon icon={faAngleDown} size="sm" className={classes.buttonMinimizedIconToggle} />
            </Button>
        </Tooltip>
    )
}

export function StatusButton({
    currentStatus,
    anchorEl,
    onStatusChange,
    hiddenStatuses = [],
    handleClose,
    placement = "bottom",
}: StatusButtonProps) {
    const { classes, cx } = useStatusMenuClasses()

    const availableStatuses = AVAILABLE_STATUSES.filter(
        (status) => !hiddenStatuses.includes(status) && status !== currentStatus,
    )

    return (
        <Popover
            disableScrollLock
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorReference="anchorPosition"
            anchorPosition={anchorEl ?? undefined}
            {...POPOVER_PLACEMENT[placement]}
        >
            <Stack direction="column" className={cx(classes.listRoot)}>
                {availableStatuses.map((status) => (
                    <StatusItem
                        key={status}
                        status={status}
                        onClick={(status) => {
                            onStatusChange(status)
                            handleClose()
                        }}
                    />
                ))}
            </Stack>
        </Popover>
    )
}
